import { fabric } from "fabric";
import deleteControlMixin from "@/bbox_canvas/delete-control.mixin";
import "@/bbox_canvas/fabric-text-baseline.patch";
import placementMixin from "@/bbox_canvas/placement.mixin";
import imageMixin from "@/bbox_canvas/image.mixin";
import tooltipMixin from "@/bbox_canvas/tooltip.mixin";
import selectionMixin from "@/bbox_canvas/selection.mixin";
import annotationsMixin from "@/bbox_canvas/annotations.mixin";
import annotationEventsMixin from "@/bbox_canvas/annotation-events.mixin";

export default class BboxCanvas {
    constructor(id, image, max_width, force_height, read_only, max_height) {
        const canvasEl = document.getElementById(id);
        if (!canvasEl || !canvasEl.parentElement) {
            throw new Error(`BboxCanvas: canvas element #${id} not found`);
        }

        if(!max_width) {
            max_width = canvasEl.parentElement.clientWidth;
        }

        this.draw_annotations = false;

        this.maxHeight = max_height;

        this.canvasWidth = Math.min(max_width, 1400);

        this.shift_pressed = false;
        this.alt_pressed = false;

        this.in_group_selection = [];
        
        // Tooltip for hover labels
        this.tooltip = null;
        this.tooltipText = null;

        this.originalCanvasWidth = this.canvasWidth
        canvasEl.setAttribute('width', this.canvasWidth + 'px')

        this.deleteIcon = "data:image/svg+xml,%3C%3Fxml version='1.0' encoding='utf-8'%3F%3E%3C!DOCTYPE svg PUBLIC '-//W3C//DTD SVG 1.1//EN' 'http://www.w3.org/Graphics/SVG/1.1/DTD/svg11.dtd'%3E%3Csvg version='1.1' id='Ebene_1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' x='0px' y='0px' width='595.275px' height='595.275px' viewBox='200 215 230 470' xml:space='preserve'%3E%3Ccircle style='fill:%23F44336;' cx='299.76' cy='439.067' r='218.516'/%3E%3Cg%3E%3Crect x='267.162' y='307.978' transform='matrix(0.7071 -0.7071 0.7071 0.7071 -222.6202 340.6915)' style='fill:white;' width='65.545' height='262.18'/%3E%3Crect x='266.988' y='308.153' transform='matrix(0.7071 0.7071 -0.7071 0.7071 398.3889 -83.3116)' style='fill:white;' width='65.544' height='262.179'/%3E%3C/g%3E%3C/svg%3E";
        this.canvas = new fabric.Canvas(id,
            {
                uniformScaling: false,
                uniScaleKey: false,
                fireRightClick: true,  // <-- enable firing of right click events
                fireMiddleClick: true, // <-- enable firing of middle click events
                stopContextMenu: true, // <--  prevent context menu from showing
            });

        // Additional context menu prevention. Fabric renders two stacked
        // canvases: the lower one (which keeps `id`) and an "upper" canvas
        // that actually receives pointer/right-click events but does not
        // carry `id` — so both need a listener, and the document-level
        // fallback below must match on the wrapper element, not `#id`.
        const wrapperEl = this.canvas.wrapperEl;
        const preventContextMenu = function(e) {
            e.preventDefault();
            e.stopPropagation();
            return false;
        };
        this.canvas.getElement().addEventListener('contextmenu', preventContextMenu);
        this.canvas.upperCanvasEl.addEventListener('contextmenu', preventContextMenu);

        // Global context menu prevention for the canvas area
        document.addEventListener('contextmenu', function(e) {
            if (wrapperEl && wrapperEl.contains(e.target)) {
                e.preventDefault();
                e.stopPropagation();
                return false;
            }
        });

        this.setPointValues();

        this.boxBorder = 2;
        /** Horizontal padding (px) each side inside the label chip (width = text + 2 * this) */
        this.labelPadX = 10;
        /** Line height multiplier for label chip (extra vertical padding around text) */
        this.labelLineHeight = 1.45;

        this.clear();

        this.canvas.on(
            'selection:cleared', function (e) {
                this2._updateBackgroundImageEvented();

                let deselected = e.deselected;
                if(!deselected) return;

                if (this2.canvas._currentTransform) {
                    return;
                }

                let annotation_ids_in_group = this2.in_group_selection.map(s=>{return s.annotation_id})

                deselected.forEach(object=>{
                    if(!annotation_ids_in_group.includes(object.annotation_id)) {
                        window.dispatchEvent(new CustomEvent("unselect_annotation", {
                            detail: {
                                id: object.annotation_id,
                            }
                        }));
                    }
                })
            });

        let this2 = this;

        this.canvas.on('selection:created', function() {
            let activeObject = this2.canvas.getActiveObject()
            if(activeObject._objects) {
                activeObject.setControlsVisibility({
                    mtr: false
                });
            }
            this2._updateBackgroundImageEvented();
        })
        this.canvas.on('selection:updated', function() {
            let activeObject = this2.canvas.getActiveObject()
            if(activeObject._objects) {
                activeObject.setControlsVisibility({
                    mtr: false
                });
            }
            this2._updateBackgroundImageEvented();
        })

        this.canvas.on('object:moving', function(e) {
            if (this2.canvas._middlePanning) {
                return;
            }
            if(e.target._objects) {
                e.target._objects.forEach((obj) => {
                    this2.moveObject(obj, e.target.left + e.target.width / 2, e.target.top + e.target.height / 2)
                    obj.onTheMove = true;
                })
            }
            else {
                this2.moveObject(e.target, 0, 0)
                e.target.onTheMove = true;
            }
        });

        this.canvas.on('object:scaling', function(e) {
            if(e.target._objects) {
                e.target._objects.forEach((obj) => {
                    obj.onTheMove = true;
                })
            }
            else {
                e.target.onTheMove = true;
            }
        });

        this.canvas.on('object:modified', function(e) {
            if(e.target._objects) {
                e.target._objects.forEach((obj) => {
                    this2.modifyObject(obj, e.target.left + e.target.width / 2, e.target.top + e.target.height / 2)
                    obj.onTheMove = false;
                })
            }
            else {
                this2.modifyObject(e.target, 0, 0)
                e.target.onTheMove = false;
            }
        });



        this.canvasId = id;

        let modelSize = 640
        this.modelSize = modelSize

        this.filtersInitialized = false;
        this.filterError = false;

        window.deleteIconImg = document.createElement('img');
        window.deleteIconImg.src = this.deleteIcon;
        this.setupDeleteControl();

        let canvas = this.canvas;
        canvas.canvasId = id;
        canvas.readOnly = read_only;
        // Reverse lookup for e2e / debugging (lower canvas keeps the element id).
        if (canvas.lowerCanvasEl) {
            canvas.lowerCanvasEl._bboxCanvas = this;
        }
        this.read_only = read_only;
        this.placementMode = false;
        this.placementInfo = null;
        this._deleteClickHandled = false;
        this.placementAnchor = null;
        this.placementHLine = null;
        this.placementVLine = null;
        this.placementDraftRect = null;
        this._spacePanKey = false;

        const canvasPointerDetail = function (opt) {
            let rect = canvas.getElement().getBoundingClientRect();

            let mouseDownX = opt.e.x - rect.left;
            let mouseDownY = opt.e.y - rect.top;

            let zoom = canvas.getZoom();
            let scale = canvas.scale;
            let adjustedX = (mouseDownX / zoom) - (canvas.viewportTransform[4] / zoom);
            let adjustedY = (mouseDownY / zoom) - (canvas.viewportTransform[5] / zoom);
            adjustedX = adjustedX / scale;
            adjustedY = adjustedY / scale;

            const target = opt.target;
            const onAnnotation = !!(target && (target.annotation || target.annotation_id));

            const pointer = canvas.getPointer(opt.e);
            const cw = this2.canvasWidth || canvas.getWidth() || 1;
            const ch = this2.canvasHeight || canvas.getHeight() || 1;

            return {
                x: mouseDownX,
                y: mouseDownY,
                scale: scale,
                zoom: zoom,
                transformX: canvas.viewportTransform[4],
                transformY: canvas.viewportTransform[5],
                original_x: adjustedX,
                original_y: adjustedY,
                normalized_x: pointer.x / cw,
                normalized_y: pointer.y / ch,
                button: opt.e.button,
                on_annotation: onAnnotation,
            };
        };

        this.canvas.on('mouse:down:before', function (opt) {
            // The only special-case on mouse down is the floating delete control.
            // Resize/scale uses Fabric's native corner hit-testing (getPointer already
            // accounts for canvas CSS-vs-internal size, retina and zoom).
            this2.tryHandleDeleteControlClick(opt);
        });

        this.canvas.on('mouse:down', function (opt) {
            const detail = canvasPointerDetail(opt);
            if (
                this2.placementMode
                && this2.placementInfo
                && this2.placementInfo.type === 'box'
                && detail.button === 0
            ) {
                this2.placementAnchor = {
                    x: detail.normalized_x,
                    y: detail.normalized_y,
                };
                this2.updatePlacementPreview(detail.normalized_x, detail.normalized_y);
            }
            window.dispatchEvent(new CustomEvent(this2.canvasId + "canvas_mousedown", { detail }));
        });

        this.canvas.on('mouse:move', function (opt) {
            if (!this2.placementMode || !this2.placementInfo) {
                return;
            }
            const detail = canvasPointerDetail(opt);
            this2.updatePlacementPreview(detail.normalized_x, detail.normalized_y);
        });

        this.canvas.on('mouse:up', function (opt) {
            const detail = canvasPointerDetail(opt);
            if (this2.placementMode) {
                this2.clearPlacementPreview();
            }
            window.dispatchEvent(new CustomEvent(this2.canvasId + "canvas_mouseup", { detail }));
        });

        if (image) {
            this.setImage(image)
        }
    }

    static annotationCanvasKey(annotation) {
        return JSON.stringify({
            id: annotation.id,
            type: annotation.type,
            coords: annotation.coords,
            label: annotation.label,
            conf: annotation.conf,
            show: annotation.show,
            color_values: annotation.color_values,
            format: annotation.format,
            transcript: annotation.transcript,
            transcript_source: annotation.transcript_source,
        });
    }

    static annotationGeometryKey(annotation) {
        return JSON.stringify({
            id: annotation.id,
            type: annotation.type,
            label: annotation.label,
            conf: annotation.conf,
            show: annotation.show,
            color_values: annotation.color_values,
            format: annotation.format,
            transcript: annotation.transcript,
            transcript_source: annotation.transcript_source,
        });
    }

    re_init() {
        this.draw_annotations = false;
        this.clear();
        let canvas = this.canvas;
        canvas.setViewportTransform([1,0,0,1,0,0]);
        this.filtersInitialized = false;
        this.filterError = false;
        this.requestRenderAll();
    }

    clear() {
        let objects = this.canvas.getObjects();
        for (let i = 0; i < objects.length; i++) {
            this.deleteObject(false, objects[i]);
        }
        this.removeTooltip();
    }

    deleteObject(eventData, target) {
        let canvas = target.canvas;
            canvas.remove(target);
            canvas.remove(target.text);
            canvas.remove(target.inner_point);
            canvas.requestRenderAll();
        }

    clone() {
        this.copy();
        this.paste();
        this._clipboard = false;
    }

    copy() {
        this.canvas.getActiveObject().clone(function (cloned) {
            this._clipboard = cloned;
        });
    }

    paste() {
        let this2 = this;
        this._clipboard.clone(function (clonedObj) {
            this2._clipboard = false;
            //this2.canvas.discardActiveObject();
            clonedObj.set({
                left: clonedObj.left + 10,
                top: clonedObj.top + 10,
                evented: true,
            });
            if (clonedObj.type === 'activeSelection') {
                // active selection needs a reference to the canvas.
                clonedObj.canvas = this2.canvas;
                clonedObj.forEachObject(function (obj) {
                    this2.canvas.add(obj);
                });
                // this should solve the unselectability
                clonedObj.setCoords();
            } else {
                this2.canvas.add(clonedObj);
            }
            this2._clipboard.top += 10;
            this2._clipboard.left += 10;
            this2.canvas.setActiveObject(clonedObj);
            this2.requestRenderAll();
        });
    }
}

// Method groups split into separate files and mixed onto the prototype.
// They rely on `this` (the BboxCanvas instance) and on sibling methods/state.
Object.assign(BboxCanvas.prototype, deleteControlMixin);
Object.assign(BboxCanvas.prototype, placementMixin);
Object.assign(BboxCanvas.prototype, imageMixin);
Object.assign(BboxCanvas.prototype, tooltipMixin);
Object.assign(BboxCanvas.prototype, selectionMixin);
Object.assign(BboxCanvas.prototype, annotationsMixin);
Object.assign(BboxCanvas.prototype, annotationEventsMixin);